import { useCallback, useRef } from 'react';
import { useSpace } from '../contexts/SpaceContext';
import { useToast } from '../contexts/ToastContext';

/**
 * Refuses a write when the user only has view access to the current space.
 *
 * RLS already stops these writes at the database, but only after the UI has
 * optimistically applied them — so a viewer would see their change appear, then
 * vanish, then a "Failed to..." toast. Failing here means nothing flickers and
 * the message explains why rather than reporting a failure.
 *
 * canEdit is true in the personal space, so this is a no-op for solo use.
 *
 *   const allowEdit = useEditGuard();
 *   const addThing = async () => {
 *     if (!allowEdit()) return null;
 *     ...
 *   };
 *
 * The returned function has a STABLE identity and reads permission through a
 * ref. That matters: most callers live inside useCallback with their own
 * dependency arrays, and a guard that changed identity when canEdit changed
 * would either need adding to seventeen of them or — far worse — be captured
 * stale, so switching from a space you can edit into one you cannot would leave
 * the old answer baked in. Which is precisely the case this exists to catch.
 *
 * This is a UX guard, not a security boundary. RLS remains the actual control:
 * anything here can be bypassed by calling the API directly.
 */
export function useEditGuard() {
  const { canEdit } = useSpace();
  const { toast } = useToast();

  const latest = useRef({ canEdit, toast });
  latest.current = { canEdit, toast };

  return useCallback((): boolean => {
    if (latest.current.canEdit) return true;
    latest.current.toast('You have view-only access to this space', 'info');
    return false;
  }, []);
}
